"use client";

import React from "react";
import { Building2, User, Mail, Phone, Briefcase, DollarSign, ShoppingCart, Loader as Loader2, CircleCheck as CheckCircle2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const businessTypes = ["Distributor", "Wholesaler", "Retail Chain", "Trader", "E-commerce Seller", "Importer", "Exporter"];
const budgetRanges = ["Less than $10,000", "$10,000 - $50,000", "$50,000 - $100,000", "$100,000 - $500,000", "More than $500,000"];
function BuyerFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-fade show">
        <div className="size-16 bg-green-100 rounded-lg flex items-center justify-center mb-6">
          <CheckCircle2 className="size-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-[var(--color-primary-900)] mb-2">
          Registration Successful
        </h3>
        <p className="text-[var(--color-primary-600)] mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-[var(--color-primary-200)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-100)]">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company_name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
                <Building2 className="size-4 text-[var(--color-primary-500)]" />
                Company Name
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Global Electronics LLC" className="border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_person" rules={{
        required: "Contact person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
                <User className="size-4 text-[var(--color-primary-500)]" />
                Contact Person
              </FormLabel>
              <FormControl>
                <Input placeholder="Full Name" className="border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Business email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
                <Mail className="size-4 text-[var(--color-primary-500)]" />
                Business Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="purchasing@company.com" className="border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
                <Phone className="size-4 text-[var(--color-primary-500)]" />
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="business_type" rules={{
        required: "Please select your business type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
                <Briefcase className="size-4 text-[var(--color-primary-500)]" />
                Business Type
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)] w-full">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {businessTypes.map((type, i) => <SelectItem key={type} value={type} data-index={i}>
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget_range" rules={{
        required: "Please select an estimated budget"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
                <DollarSign className="size-4 text-[var(--color-primary-500)]" />
                Estimated Monthly Budget
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)] w-full">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {budgetRanges.map((range, i) => <SelectItem key={range} value={range} data-index={i}>
                      {range}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="buying_requirements" rules={{
      required: "Please detail your buying requirements"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="flex items-center gap-2 text-[var(--color-primary-800)]">
              <ShoppingCart className="size-4 text-[var(--color-primary-500)]" />
              Specific Buying Requirements
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Please list the brands, models, quantities, and any specific conditions (e.g., Brand New, CPO, Used) you are looking for..." className="min-h-[120px] border-[var(--color-primary-200)] focus-visible:ring-[var(--color-primary-500)] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto btn-primary text-base px-8 py-6 h-auto">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-5" />
              Submitting Request...
            </> : 'Submit Buying Request'}
        </Button>
        <p className="text-xs text-[var(--color-primary-400)] mt-4 text-center md:text-left">
          By submitting this form, you agree to our terms of service and privacy policy. A dedicated account manager will contact you within 24 hours.
        </p>
      </div>
    </Form>;
}
export default function BuyerRegistration() {
  return <section id="buyer-registration-form" className="py-16 md:py-24 bg-[var(--color-background)]">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-[var(--color-primary-900)] mb-4">
            Become a Verified Buyer
          </h2>
          <p className="text-lg text-[var(--color-primary-600)] max-w-2xl mx-auto">
            Join our global network of distributors and retailers. Gain access to exclusive wholesale pricing, daily stock lists, and dedicated account management.
          </p>
        </div>

        <Card className="card border-0 shadow-lg bg-white overflow-hidden">
          <div className="h-2 w-full bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-accent-500)]"></div>
          <CardHeader className="pb-8 pt-8 px-6 md:px-10 border-b border-[var(--color-primary-100)] bg-[var(--color-primary-100)]/30">
            <CardTitle className="text-2xl font-default text-[var(--color-primary-900)]">
              Buyer Registration Form
            </CardTitle>
            <CardDescription className="text-[var(--color-primary-600)] text-base">
              Please provide your business details below to help us tailor our offerings to your specific needs.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-10">
            <FormProvider formId="buyer-registration-main" sectionName="Buyer Registration Section" defaultValues={{
            company_name: "",
            contact_person: "",
            email: "",
            phone: "",
            business_type: "",
            budget_range: "",
            buying_requirements: ""
          }} successMessage="Thank you for registering! Your request has been received and a dedicated account manager will contact you shortly.">
              <BuyerFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}